import React, { useState, useEffect } from 'react';
import { Calendar, Users, Clock, DollarSign, Settings, Bell, History } from 'lucide-react';
import { GoogleCalendarConnect } from '../GoogleCalendarConnect';
import { MasterCalendar } from '../MasterCalendar';
import { useLanguage } from '../../contexts/LanguageContext';
import { translate } from '../../locales';
import './MasterDashboard.css';

export interface Client {
  id: string;
  name: string;
  email?: string;
  phone: string;
}

export interface Service {
  id: string;
  name: string;
  description?: string;
  duration_minutes?: number;
}

export interface Appointment {
  id: string;
  client_id: string;
  client?: Client;
  service_id?: string;
  service?: Service;
  appointment_date: string;
  duration_minutes?: number;
  total_price?: number;
  status: string;
  notes?: string;
  payment_status?: string;
  payment_method?: string;
}

interface DashboardStats {
  totalAppointments: number;
  upcomingAppointments: number;
  completedAppointments: number;
  totalRevenue: number;
}

interface MasterDashboardProps {
  masterId: string;
}

export const MasterDashboard: React.FC<MasterDashboardProps> = ({
  masterId,
}) => {
  console.log('🎯 MasterDashboard rendered with masterId:', masterId);
  console.log('🎯 MasterDashboard rendered - timestamp:', new Date().toISOString());
  
  const { language } = useLanguage();
  
  const [activeTab, setActiveTab] = useState<'overview' | 'appointments' | 'calendar' | 'settings'>('overview');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalAppointments: 0,
    upcomingAppointments: 0,
    completedAppointments: 0,
    totalRevenue: 0,
  });
  const [isCalendarConnected, setIsCalendarConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sync tab state with URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    
    if (tabParam === 'appointments') {
      setActiveTab('appointments');
    } else if (tabParam === 'calendar') {
      setActiveTab('calendar');
    } else if (tabParam === 'settings') {
      setActiveTab('settings');
    } else {
      setActiveTab('overview');
    }
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const tabParam = urlParams.get('tab');
      
      if (tabParam === 'appointments') {
        setActiveTab('appointments');
      } else if (tabParam === 'calendar') {
        setActiveTab('calendar');
      } else if (tabParam === 'settings') {
        setActiveTab('settings');
      } else {
        setActiveTab('overview');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Update URL when tab changes
  const updateTabUrl = (tab: 'overview' | 'appointments' | 'calendar' | 'settings') => {
    const url = new URL(window.location.href);
    if (tab === 'appointments') {
      url.searchParams.set('tab', 'appointments');
    } else if (tab === 'calendar') {
      url.searchParams.set('tab', 'calendar');
    } else if (tab === 'settings') {
      url.searchParams.set('tab', 'settings');
    } else {
      url.searchParams.delete('tab');
    }
    window.history.pushState({}, '', url.toString());
  };

  const handleTabChange = (tab: 'overview' | 'appointments' | 'calendar' | 'settings') => {
    setActiveTab(tab);
    updateTabUrl(tab);
  };

  useEffect(() => {
    fetchDashboardData();
    checkCalendarConnection();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('auth_token'); // Змінено з 'token' на 'auth_token'
      console.log('🔑 MasterDashboard - Token from localStorage:', token);
      
      if (!token) {
        console.error('🔑 MasterDashboard - No token found in localStorage');
        setError(translate('masterDashboard.error.noToken', language));
        setIsLoading(false);
        return;
      }
      
      console.log('🔑 MasterDashboard - Fetching appointments with token:', token.substring(0, 20) + '...');
      
      // Fetch appointments
      const appointmentsResponse = await fetch('/api/v1/appointments', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('🔑 MasterDashboard - Response status:', appointmentsResponse.status);

      if (appointmentsResponse.ok) {
        const appointmentsData = await appointmentsResponse.json();
        console.log('🔑 MasterDashboard - Appointments data:', appointmentsData);
        setAppointments(appointmentsData.appointments || appointmentsData || []);
        
        // Calculate stats
        const allAppointments = appointmentsData.appointments || appointmentsData || [];
        const upcoming = allAppointments.filter((apt: Appointment) => 
          apt.status === 'pending' || apt.status === 'confirmed'
        );
        const completed = allAppointments.filter((apt: Appointment) => 
          apt.status === 'completed'
        );
        const revenue = completed.reduce((sum: number, apt: Appointment) => 
          sum + (apt.total_price || 0), 0
        );

        setStats({
          totalAppointments: allAppointments.length,
          upcomingAppointments: upcoming.length,
          completedAppointments: completed.length,
          totalRevenue: revenue,
        });
      } else {
        console.error('Failed to fetch appointments:', appointmentsResponse.statusText);
        const errorText = await appointmentsResponse.text();
        console.error('Error response:', errorText);
        setError('Failed to fetch appointments');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Error loading dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const checkCalendarConnection = async () => {
    try {
      const token = localStorage.getItem('auth_token'); // Змінено з 'token' на 'auth_token'
      const response = await fetch('/api/v1/calendar/status', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsCalendarConnected(data.connected);
      }
    } catch (error) {
      console.error('Error checking calendar connection:', error);
    }
  };

  const handleAppointmentAction = async (appointmentId: string, action: 'confirm' | 'cancel') => {
    try {
      const token = localStorage.getItem('auth_token'); // Змінено з 'token' на 'auth_token'
      const response = await fetch(`/api/v1/appointments/${appointmentId}/${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchDashboardData(); // Refresh data
      }
    } catch (error) {
      console.error(`Error ${action}ing appointment:`, error);
    }
  };

  const isAppointmentPast = (appointmentDate: string): boolean => {
    const appointmentDateTime = new Date(appointmentDate);
    const now = new Date();
    return appointmentDateTime < now;
  };

  const renderOverview = () => (
    <div className="dashboard-section">
      <h2 className="dashboard-section-title">{translate('masterDashboard.overview.title', language)}</h2>
      
      {/* Stats Cards */}
      <div className="dashboard-stats-grid">
        <div className="dashboard-stat-card">
          <div className="dashboard-stat-content">
            <div className="dashboard-stat-info">
              <p className="dashboard-stat-label">{translate('masterDashboard.overview.stats.totalAppointments', language)}</p>
              <p className="dashboard-stat-value">{stats.totalAppointments}</p>
            </div>
            <Calendar className="dashboard-stat-icon blue" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="dashboard-stat-content">
            <div className="dashboard-stat-info">
              <p className="dashboard-stat-label">{translate('masterDashboard.overview.stats.upcoming', language)}</p>
              <p className="dashboard-stat-value">{stats.upcomingAppointments}</p>
            </div>
            <Clock className="dashboard-stat-icon orange" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="dashboard-stat-content">
            <div className="dashboard-stat-info">
              <p className="dashboard-stat-label">{translate('masterDashboard.overview.stats.completed', language)}</p>
              <p className="dashboard-stat-value">{stats.completedAppointments}</p>
            </div>
            <DollarSign className="dashboard-stat-icon green" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="dashboard-stat-content">
            <div className="dashboard-stat-info">
              <p className="dashboard-stat-label">{translate('masterDashboard.overview.stats.totalRevenue', language)}</p>
              <p className="dashboard-stat-value">₴{stats.totalRevenue}</p>
            </div>
            <DollarSign className="dashboard-stat-icon purple" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderAppointments = () => (
    <div className="dashboard-section">
      <h2 className="dashboard-section-title">{translate('masterDashboard.appointments.title', language)}</h2>
      
      <div className="dashboard-table-container">
        <table className="dashboard-table">
          <thead>
            <tr>
              <th>{translate('masterDashboard.appointments.table.headers.client', language)}</th>
              <th>{translate('masterDashboard.appointments.table.headers.service', language)}</th>
              <th>{translate('masterDashboard.appointments.table.headers.dateTime', language)}</th>
              <th>{translate('masterDashboard.appointments.table.headers.status', language)}</th>
              <th>{translate('masterDashboard.appointments.table.headers.price', language)}</th>
              <th>{translate('masterDashboard.appointments.table.headers.actions', language)}</th>
            </tr>
          </thead>
          <tbody>
            {appointments.map((appointment) => {
              const isPast = isAppointmentPast(appointment.appointment_date);
              
              return (
                <tr key={appointment.id} className={isPast ? 'past-appointment' : ''}>
                  <td>
                    <div className="dashboard-table-client-name">
                      {appointment.client?.name || 'Unknown Client'}
                    </div>
                    {appointment.client?.email && (
                      <div className="dashboard-table-client-email">{appointment.client.email}</div>
                    )}
                  </td>
                  <td>
                    <div className="dashboard-table-service-name">
                      {appointment.service?.name || 'Unknown Service'}
                    </div>
                  </td>
                  <td>
                    <div className="dashboard-table-date">
                      {new Date(appointment.appointment_date).toLocaleDateString()}
                    </div>
                    <div className="dashboard-table-time">
                      {new Date(appointment.appointment_date).toLocaleTimeString()}
                    </div>
                  </td>
                  <td>
                    <div className="dashboard-status-container">
                      <span className={`dashboard-status-badge ${appointment.status}`}>
                        {appointment.status}
                      </span>
                      {isPast && (
                        <div className="past-appointment-indicator">
                          <History className="h-3 w-3" />
                          <span>Пройдено</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="dashboard-table-price">
                    ₴{appointment.total_price || 0}
                  </td>
                  <td>
                    <div className="dashboard-table-actions">
                      {appointment.status === 'pending' && !isPast && (
                        <button
                          onClick={() => handleAppointmentAction(appointment.id, 'confirm')}
                          className="dashboard-table-action-button confirm"
                        >
                          {translate('masterDashboard.appointments.table.actions.confirm', language)}
                        </button>
                      )}
                      {(appointment.status === 'confirmed' || appointment.status === 'completed') && !isPast && (
                        <button
                          onClick={() => handleAppointmentAction(appointment.id, 'cancel')}
                          className="dashboard-table-action-button cancel"
                        >
                          {translate('masterDashboard.appointments.table.actions.cancel', language)}
                        </button>
                      )}
                      {isPast && (
                        <button
                          disabled
                          className="dashboard-table-action-button disabled"
                        >
                          Пройдено
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCalendar = () => (
    <div className="dashboard-section">
      <h2 className="dashboard-section-title">{translate('masterDashboard.calendar.title', language)}</h2>
      
      {/* Google Calendar Integration */}
      <div className="dashboard-calendar-integration">
        <h3 className="dashboard-calendar-title">{translate('masterDashboard.calendar.integration.title', language)}</h3>
        <GoogleCalendarConnect
          isConnected={isCalendarConnected}
          onConnect={() => setIsCalendarConnected(true)}
          onDisconnect={() => setIsCalendarConnected(false)}
        />
      </div>

      {/* Calendar View */}
      <MasterCalendar appointments={appointments} />
    </div>
  );

  const renderSettings = () => (
    <div className="dashboard-section">
      <h2 className="dashboard-section-title">{translate('masterDashboard.settings.title', language)}</h2>
      <div className="dashboard-settings-container">
        <h3 className="dashboard-settings-title">{translate('masterDashboard.settings.account.title', language)}</h3>
        <div className="dashboard-settings-form">
          <div>
            <label className="dashboard-settings-label">
              {translate('masterDashboard.settings.account.masterId', language)}
            </label>
            <input
              type="text"
              value={masterId}
              disabled
              className="dashboard-settings-input"
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="dashboard-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="dashboard-error-content">
          <h2 className="dashboard-error-title">{translate('masterDashboard.error.title', language)}</h2>
          <p className="dashboard-error-message">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="dashboard-error-button"
          >
            {translate('masterDashboard.error.retry', language)}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="dashboard-header-inner">
            <h5 className="dashboard-title">{translate('masterDashboard.title', language)}</h5>
            <div className="dashboard-header-actions">
              <button className="dashboard-header-button">
                <Bell className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Card */}
      <div className="dashboard-content-card">
        {/* Tab Navigation */}
        <nav className="dashboard-nav">
          <div className="dashboard-nav-content">
            <div className="dashboard-nav-tabs">
              {[
                { id: 'overview', label: translate('masterDashboard.tabs.overview', language), icon: Calendar },
                { id: 'appointments', label: translate('masterDashboard.tabs.appointments', language), icon: Users },
                { id: 'calendar', label: translate('masterDashboard.tabs.calendar', language), icon: Calendar },
                { id: 'settings', label: translate('masterDashboard.tabs.settings', language), icon: Settings },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id as any)}
                  className={`dashboard-nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                >
                  <tab.icon className="dashboard-nav-icon" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </nav>

        {/* Tab Content */}
        <main className="dashboard-main">
          <div className="dashboard-section">
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'appointments' && renderAppointments()}
            {activeTab === 'calendar' && renderCalendar()}
            {activeTab === 'settings' && renderSettings()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default MasterDashboard;
